import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import sessionsEmployeesReportApi, {
  type SessionsEmployeesReportResponseI,
} from 'Api/axios/sessions-employees-report-api';
import type { SessionsReportFiltersI } from 'Api/axios/sessions-report-api';
import type { ReportsPaginationI } from 'Pages/reports/components/ReportsDataGrid/types';
import { downloadFile } from 'Utils/file-utils';
import { s3 } from 'Services/AWS/s3';
import useTranslation from 'Hooks/useTranslation';
import type { SessionsReportFilterValues } from '../schemas/sessions-report-schemas';

const toApiFilters = (
  values: SessionsReportFilterValues,
  page: number,
  pageSize: number
): SessionsReportFiltersI => {
  const { search, startDate, endDate, ...rest } = values;
  return {
    ...rest,
    search: search?.trim() || undefined,
    startDate: startDate ? format(startDate, 'yyyy-MM-dd') : undefined,
    endDate: endDate ? format(endDate, 'yyyy-MM-dd') : undefined,
    page,
    limit: pageSize,
  };
};

const useSessionEmployeesPage = () => {
  const { t2 } = useTranslation('SessionsReport');
  const [report, setReport] = useState<SessionsEmployeesReportResponseI | null>(null);
  const [pagination, setPagination] = useState<ReportsPaginationI | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [expandedEmployeeId, setExpandedEmployeeId] = useState<string | null>(null);
  const [currentFilters, setCurrentFilters] = useState<SessionsReportFilterValues | null>(null);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  const fetchReport = useCallback(
    async (values: SessionsReportFilterValues, pageNum?: number, pageSizeNum?: number) => {
      const currentPage = pageNum ?? 1;
      const currentPageSize = pageSizeNum ?? pageSize;
      setPage(currentPage);
      setCurrentFilters(values);
      setLoading(true);
      setError(null);
      const response = await sessionsEmployeesReportApi.fetchReport(
        toApiFilters(values, currentPage, currentPageSize)
      );
      if (response.error) {
        setError(response.error);
        setReport(null);
        setPagination(null);
      } else {
        setReport(response.data || null);
        if (response.pagination) {
          setPagination({
            page: response.pagination.page,
            pageSize: response.pagination.pageSize,
            totalItems: response.pagination.totalItems,
            totalPages: response.pagination.totalPages,
          });
        }
      }
      setLoading(false);
      return response;
    },
    [pageSize]
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      setPage(newPage);
      if (currentFilters) {
        fetchReport(currentFilters, newPage, pageSize);
      }
    },
    [currentFilters, pageSize, fetchReport]
  );

  const handleRowsPerPageChange = useCallback(
    (newPageSize: number) => {
      setPage(1);
      setPageSize(newPageSize);
      if (currentFilters) {
        fetchReport(currentFilters, 1, newPageSize);
      }
    },
    [currentFilters, fetchReport]
  );

  const toggleExpanded = useCallback((employeeId: string) => {
    setExpandedEmployeeId((prev) => (prev === employeeId ? null : employeeId));
  }, []);

  const handleExport = useCallback(async () => {
    if (!currentFilters) return;
    setExporting(true);
    const { page: _p, limit: _l, ...exportFilters } = toApiFilters(currentFilters, 1, 1);
    const response = await sessionsEmployeesReportApi.exportReport(exportFilters);
    setExporting(false);
    if (response.error) {
      toast.error(response.error);
      return;
    }
    if (response.data) {
      downloadFile(response.data, `reporte-sesiones-empleados-${Date.now()}.xlsx`);
      toast.success(t2('toasts.reportExported'));
    }
  }, [currentFilters, t2]);

  const downloadEvidence = useCallback(
    async (fileUrl: string, fileName: string) => {
      try {
        const response = await s3.getSignedUrl(fileUrl);
        const signedUrl = response?.signedUrl;
        if (!signedUrl) {
          toast.error(t2('toasts.evidenceDownloadFailed'));
          return;
        }
        const fileResponse = await fetch(signedUrl);
        if (!fileResponse.ok) throw new Error('Download failed');
        const blob = await fileResponse.blob();
        downloadFile(blob, fileName);
      } catch {
        toast.error(t2('toasts.evidenceDownloadFailed'));
      }
    },
    [t2]
  );

  return {
    report,
    pagination,
    loading,
    error,
    exporting,
    expandedEmployeeId,
    fetchReport,
    toggleExpanded,
    handlePageChange,
    handleRowsPerPageChange,
    handleExport,
    downloadEvidence,
    t2,
  };
};

export default useSessionEmployeesPage;
